"use client";

import { useEffect, useState, useRef, lazy, Suspense } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Menu, X, Search, Moon, Sun, ExternalLink, HelpCircle, Command, ArrowRight } from "lucide-react";
import { mainNav, siteConfig } from "@/lib/data/site-config";
import { setStorageItem } from "@/lib/client-storage";
import { cn } from "@/lib/utils";
import { Logomark, Wordmark } from "@/components/ui/logomark";
import { Badge } from "@/components/ui/badge";

const SearchOverlay = lazy(() => import("./search-overlay").then((m) => ({ default: m.SearchOverlay })));
const CategoriesMegaMenu = lazy(() => import("./categories-mega-menu").then((m) => ({ default: m.CategoriesMegaMenu })));

const NAV_SHORTCUTS: Record<string, string> = {
  "1": "/tools",
  "2": "/categories",
  "3": "/guides",
  "4": "/blog",
};

export function Header() {
  const router = useRouter();
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuPanelRef = useRef<HTMLDivElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 8);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      const isTyping =
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA" ||
        document.activeElement?.getAttribute("contenteditable") === "true";
      if (e.key === "/" && !isTyping && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && NAV_SHORTCUTS[e.key] && !isTyping) {
        e.preventDefault();
        router.push(NAV_SHORTCUTS[e.key]);
        return;
      }
      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (!isTyping) {
          e.preventDefault();
          setSearchOpen(true);
        }
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [router]);

  // Mobile sheet a11y: move focus inside on open, return it on close.
  const wasMobileOpen = useRef(false);
  useEffect(() => {
    if (isMobileMenuOpen) {
      wasMobileOpen.current = true;
      mobileMenuPanelRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    } else if (wasMobileOpen.current) {
      wasMobileOpen.current = false;
      mobileMenuButtonRef.current?.focus();
    }
  }, [isMobileMenuOpen]);

  const toggleDark = () => {
    const isDark = document.documentElement.classList.toggle("dark");
    setStorageItem("theme", isDark ? "dark" : "light");
  };

  const handleSearchClick = () => {
    setSearchOpen(true);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b border-[var(--color-border)] bg-[var(--color-bg)]/85 backdrop-blur-md",
        scrolled && "shadow-[var(--shadow-sm)]"
      )}
      role="banner"
    >
      <div className="h-0.5 w-full bg-[var(--color-accent)]" aria-hidden="true" />
      <div className="container flex min-h-[var(--header-height-compact)] items-center justify-between gap-2.5 py-1.5">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <Link
            href="/"
            className="flex shrink-0 items-center gap-2 rounded-lg px-1 py-0.5"
            aria-label="DevStackIO Tools Home"
          >
            <Logomark size="sm" />
            <Wordmark className="block truncate text-lg" />
            <Badge className="hidden bg-[var(--color-surface-2)] text-[var(--color-text-muted)] sm:inline-flex">
              Tools
            </Badge>
          </Link>

          <nav
            className="hidden items-center gap-1 xl:flex"
            aria-label="Main navigation"
            role="navigation"
          >
            <ul className="flex items-center gap-0.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-1 shadow-[var(--shadow-xs)]" role="menubar">
              {mainNav.map((item) => {
                const isActive = pathname === item.href || (item.href !== "/" && pathname?.startsWith(item.href));
                return (
                  <li key={item.href} role="none">
                    <Link
                      href={item.href}
                      role="menuitem"
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "flex h-9 items-center whitespace-nowrap rounded-lg px-3 text-sm transition-colors touch-target",
                        isActive
                          ? "bg-[var(--color-accent-strong)] font-semibold text-[var(--color-accent-fg)]"
                          : "font-medium text-[var(--color-text-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                      )}
                    >
                      {item.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Suspense fallback={null}>
              <CategoriesMegaMenu />
            </Suspense>
          </nav>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={handleSearchClick}
            aria-label="Search tools (Ctrl+K)"
            className="hidden h-10 min-w-[200px] max-w-[300px] flex-1 items-center gap-2 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3.5 text-sm text-[var(--color-text-muted)] shadow-[var(--shadow-xs)] transition-colors hover:border-[var(--color-border-strong)] lg:flex"
            type="button"
          >
            <Search className="h-4 w-4 shrink-0 text-[var(--color-accent-strong)]" aria-hidden="true" />
            <span className="truncate">What do you need to do?</span>
            <kbd className="ml-auto hidden h-6 shrink-0 items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-2)] px-2 text-[11px] font-bold text-[var(--color-text-muted)] lg:inline-flex" aria-hidden="true">
              <Command className="h-3 w-3" aria-hidden="true" />
              K
            </kbd>
          </button>

          <Link
            href="/tools"
            className="hidden h-10 items-center gap-1.5 rounded-xl bg-[var(--color-accent-strong)] px-4 text-sm font-semibold text-[var(--color-accent-fg)] transition-colors hover:bg-[var(--color-accent-hover)] md:inline-flex"
          >
            Browse tools
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>

          <button
            onClick={toggleDark}
            aria-label="Toggle dark mode"
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)] shadow-[var(--shadow-xs)] transition-colors hover:border-[var(--color-border-strong)] hover:text-[var(--color-text)] touch-target"
            suppressHydrationWarning
            type="button"
          >
            <span className="hidden dark:inline" suppressHydrationWarning aria-hidden="true"><Sun className="h-5 w-5" aria-hidden="true" /></span>
            <span className="inline dark:hidden" suppressHydrationWarning aria-hidden="true"><Moon className="h-5 w-5" aria-hidden="true" /></span>
          </button>

          <button
            onClick={handleSearchClick}
            aria-label="Search tools (open search)"
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)] touch-target xl:hidden"
            type="button"
          >
            <Search className="h-5 w-5" aria-hidden="true" />
          </button>

          <button
            ref={mobileMenuButtonRef}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-panel"
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--color-accent-strong)] text-[var(--color-accent-fg)] transition-colors hover:bg-[var(--color-accent-hover)] touch-target xl:hidden"
            type="button"
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          id="mobile-navigation-panel"
          ref={mobileMenuPanelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Main navigation"
          className="border-t border-[var(--color-border)] bg-[var(--color-bg)] xl:hidden animate-slide-down"
        >
          <nav className="container space-y-1 py-3" aria-label="Mobile navigation">
            <ul role="list" className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-sm)]">
              {mainNav.map((item) => (
                <li key={item.href} className="border-b border-[var(--color-border)] last:border-b-0">
                  <Link
                    href={item.href}
                    onClick={closeMobileMenu}
                    className="flex items-center justify-between px-4 py-3 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)] touch-target"
                  >
                    {item.title}
                    <ArrowRight className="h-4 w-4 text-[var(--color-text-subtle)]" aria-hidden="true" />
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  href="/categories"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-between px-4 py-3 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)] touch-target"
                >
                  Categories
                  <ArrowRight className="h-4 w-4 text-[var(--color-text-subtle)]" aria-hidden="true" />
                </Link>
              </li>
            </ul>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => { closeMobileMenu(); setSearchOpen(true); }}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm font-medium text-[var(--color-text-muted)] touch-target"
                type="button"
              >
                <Search className="h-4 w-4" aria-hidden="true" />
                Search
              </button>
              <button
                onClick={() => { closeMobileMenu(); setSearchOpen(true); }}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm font-medium text-[var(--color-text-muted)] touch-target"
                type="button"
              >
                <HelpCircle className="h-4 w-4" aria-hidden="true" />
                Shortcuts
              </button>
              <a
                href={siteConfig.mainSiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm font-medium text-[var(--color-text-muted)] touch-target"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                DevStackIO
              </a>
            </div>
          </nav>
        </div>
      )}

      <Suspense fallback={null}>
        <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      </Suspense>
    </header>
  );
}
